import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Satellite } from '../types/satellite';
interface EarthVisualizationProps {
  satellites: Satellite[];
}
const EarthVisualization: React.FC<EarthVisualizationProps> = ({
  satellites
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const satelliteObjectsRef = useRef<THREE.Object3D[]>([]);
  // Initialize the 3D scene
  useEffect(() => {
    if (!containerRef.current) return;
    // Create scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    // Create camera
    const camera = new THREE.PerspectiveCamera(45, containerRef.current.clientWidth / containerRef.current.clientHeight, 0.1, 1000);
    camera.position.z = 15;
    cameraRef.current = camera;
    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setClearColor(0x000000);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);
    // Add directional light (sun-like)
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 3, 5);
    scene.add(directionalLight);
    // Create Earth
    const earthGeometry = new THREE.SphereGeometry(5, 64, 64);
    // Load Earth texture
    const textureLoader = new THREE.TextureLoader();
    const earthTexture = textureLoader.load('https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/planets/earth_atmos_2048.jpg');
    const earthMaterial = new THREE.MeshPhongMaterial({
      map: earthTexture,
      specular: 0x333333,
      shininess: 5
    });
    const earth = new THREE.Mesh(earthGeometry, earthMaterial);
    scene.add(earth);
    // Add stars background
    const starGeometry = new THREE.BufferGeometry();
    const starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.1
    });
    const starVertices = [];
    for (let i = 0; i < 10000; i++) {
      const x = (Math.random() - 0.5) * 2000;
      const y = (Math.random() - 0.5) * 2000;
      const z = (Math.random() - 0.5) * 2000;
      starVertices.push(x, y, z);
    }
    starGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starVertices, 3));
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);
    // Add orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 0.5;
    controlsRef.current = controls;
    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      // Slowly rotate the Earth
      earth.rotation.y += 0.001;
      controls.update();
      renderer.render(scene, camera);
    };
    animate();
    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && rendererRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
      }
    };
  }, []);
  // Update satellites when they change
  useEffect(() => {
    if (!sceneRef.current) return;
    // Remove previous satellites
    satelliteObjectsRef.current.forEach(obj => {
      sceneRef.current?.remove(obj);
    });
    satelliteObjectsRef.current = [];
    // Add new satellites
    satellites.forEach(satellite => {
      // Create a small sphere for each satellite
      const size = 0.05;
      const geometry = new THREE.SphereGeometry(size, 8, 8);
      // Color based on satellite purpose
      let color;
      switch (satellite.purpose) {
        case 'Communication':
          color = 0x00ff00; // Green
          break;
        case 'Navigation':
          color = 0xffff00; // Yellow
          break;
        case 'Military':
          color = 0xff0000; // Red
          break;
        case 'Earth Observation':
          color = 0x0000ff; // Blue
          break;
        default:
          color = 0xffffff;
        // White
      }
      const material = new THREE.MeshBasicMaterial({
        color
      });
      const mesh = new THREE.Mesh(geometry, material);
      // Position the satellite based on its orbital parameters
      const radius = 5 + satellite.altitude / 1000; // Scale altitude
      const phi = Math.random() * Math.PI * 2;
      const theta = Math.random() * Math.PI;
      mesh.position.x = radius * Math.sin(theta) * Math.cos(phi);
      mesh.position.y = radius * Math.sin(theta) * Math.sin(phi);
      mesh.position.z = radius * Math.cos(theta);
      // Add orbit line
      const orbitPoints = [];
      for (let i = 0; i <= 64; i++) {
        const orbitPhi = i / 64 * Math.PI * 2;
        orbitPoints.push(new THREE.Vector3(radius * Math.sin(theta) * Math.cos(orbitPhi), radius * Math.sin(theta) * Math.sin(orbitPhi), radius * Math.cos(theta)));
      }
      const orbitGeometry = new THREE.BufferGeometry().setFromPoints(orbitPoints);
      const orbitMaterial = new THREE.LineBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.3
      });
      const orbit = new THREE.Line(orbitGeometry, orbitMaterial);
      // Add to scene and tracking array
      sceneRef.current.add(mesh);
      sceneRef.current.add(orbit);
      satelliteObjectsRef.current.push(mesh, orbit);
    });
  }, [satellites]);
  return <div ref={containerRef} className="w-full h-full" data-id="element-14" />;
};
export default EarthVisualization;